<script setup lang="ts">
import { ref, computed } from 'vue'
import AdminSectorForm from '@/views/forms/AdminSectorForm.vue'
import { useAdmin } from '@/stores/admin'
import type { PlatformAdvertiser } from '@/stores/admin'
import { handleError } from '@/lib/error'
import { formatPlatformName } from '@/lib/utils'

const props = defineProps<{
  advertiser: (PlatformAdvertiser & { brand_name: string }) | null
  onRefreshAll?: () => Promise<void>
}>()

const emit = defineEmits<{
  close: []
  updated: [advertiser: PlatformAdvertiser]
}>()

const admin = useAdmin()

const isToggling = ref(false)
const showCreateSectorModal = ref(false)
const showScopeModal = ref(false)
const pendingSector = ref<{ id: string; name: string; emoji?: string } | null>(null)

const brand = computed(() => admin.brands.find((b) => b.id === props.advertiser?.brand_id))

// Sectors the advertiser currently has (inherited or explicit)
const advertiserSectorIds = computed(() => new Set(props.advertiser?.sectors.map((s) => s.id)))
// Sectors explicitly set on advertiser (not just inherited)
const explicitSectorIds = computed(
  () =>
    new Set(props.advertiser?.sectors.filter((s) => s.source === 'advertiser').map((s) => s.id)),
)
// All brand sector IDs
const brandSectorIds = computed(() => new Set(brand.value?.sectors.map((s) => s.id)))
// Excluded = brand sectors NOT in advertiser sectors
const excludedSectorIds = computed(() => {
  const excluded = new Set<string>()
  for (const s of brand.value?.sectors ?? []) {
    if (!advertiserSectorIds.value.has(s.id)) {
      excluded.add(s.id)
    }
  }
  return excluded
})

const brandSectors = computed(() => admin.sectors.filter((s) => brandSectorIds.value.has(s.id)))
const otherSectors = computed(() => admin.sectors.filter((s) => !brandSectorIds.value.has(s.id)))

async function toggleBrandSector(sectorId: string) {
  if (!props.advertiser || isToggling.value) return
  isToggling.value = true
  const { brand_id, id } = props.advertiser
  try {
    let updated: PlatformAdvertiser
    if (excludedSectorIds.value.has(sectorId)) {
      // Currently excluded → unexclude (re-inherit)
      updated = await admin.unexcludeAdvertiserSector(brand_id, id, sectorId)
    } else {
      // Currently included → exclude
      updated = await admin.excludeAdvertiserSector(brand_id, id, sectorId)
    }
    emit('updated', updated)
  } catch (err) {
    handleError(err, 'Failed to update sector')
  } finally {
    isToggling.value = false
  }
}

async function addOtherSector(sectorId: string) {
  if (!props.advertiser || isToggling.value) return
  isToggling.value = true
  const { brand_id, id } = props.advertiser
  try {
    await admin.addAdvertiserSector(brand_id, id, sectorId)
    // Adding may have added the sector to the brand too - refresh all advertisers
    admin.invalidateCache('brands')
    if (props.onRefreshAll) {
      await props.onRefreshAll()
    }
  } catch (err) {
    handleError(err, 'Failed to add sector')
  } finally {
    isToggling.value = false
  }
}

async function handleCreateSector(data: { name: string; slug?: string; emoji?: string }) {
  try {
    const sector = await admin.createSector({ name: data.name, slug: data.slug, emoji: data.emoji })
    showCreateSectorModal.value = false
    pendingSector.value = sector
    showScopeModal.value = true
  } catch (err) {
    handleError(err, 'Failed to create sector')
  }
}

async function applySectorToAdvertiser() {
  if (!pendingSector.value || !props.advertiser) return
  showScopeModal.value = false
  isToggling.value = true
  const { brand_id, id: advertiserId } = props.advertiser
  const sectorId = pendingSector.value.id
  try {
    // Add sector to this advertiser (also adds to brand if not there)
    await admin.addAdvertiserSector(brand_id, advertiserId, sectorId)
    // Exclude for all OTHER advertisers of this brand
    const allAdvertisers = await admin.fetchBrandAdvertisers(brand_id)
    const otherAdvertisers = allAdvertisers.filter((a) => a.id !== advertiserId)
    await Promise.all(
      otherAdvertisers.map((a) => admin.excludeAdvertiserSector(brand_id, a.id, sectorId)),
    )
    admin.invalidateCache('brands')
    if (props.onRefreshAll) {
      await props.onRefreshAll()
    }
  } catch (err) {
    handleError(err, 'Failed to add sector')
  } finally {
    isToggling.value = false
    pendingSector.value = null
  }
}

async function applySectorToBrand() {
  if (!pendingSector.value || !brand.value || !props.advertiser) return
  showScopeModal.value = false
  isToggling.value = true
  try {
    await admin.addBrandSector(brand.value.id, pendingSector.value.id)
    admin.invalidateCache('brands')
    // Await refresh so UI updates before we finish
    if (props.onRefreshAll) {
      await props.onRefreshAll()
    }
  } catch (err) {
    handleError(err, 'Failed to add sector to brand')
  } finally {
    isToggling.value = false
    pendingSector.value = null
  }
}
</script>

<template>
  <div class="p-6 space-y-6 h-full overflow-y-auto" v-if="advertiser">
    <!-- Header -->
    <div class="flex items-start justify-between">
      <div>
        <h2 class="text-xl font-semibold font-mono">
          {{ advertiser.platform_advertiser_identifier }}
        </h2>
        <p class="text-sm text-muted">
          {{ formatPlatformName(advertiser.platform_name) }}
          · {{ advertiser.country_name ?? 'Global' }}
          <template v-if="advertiser.search_type === 'advertiser_name'">· by name</template>
        </p>
      </div>
      <UButton icon="i-lucide-x" color="neutral" variant="ghost" @click="emit('close')" />
    </div>

    <!-- Brand -->
    <div class="flex items-center justify-between p-3 rounded-md bg-elevated">
      <div class="flex items-center gap-3">
        <UAvatar :src="brand?.logo_url ?? undefined" :alt="advertiser.brand_name" size="sm" />
        <div>
          <p class="font-medium">{{ advertiser.brand_name }}</p>
          <p class="text-xs text-muted">{{ brand?.sectors.length ?? 0 }} brand sectors</p>
        </div>
      </div>
      <UButton
        icon="i-lucide-pencil"
        color="neutral"
        variant="soft"
        size="sm"
        :to="`/admin/brands#${brand?.slug}`"
      >
        Edit on brand
      </UButton>
    </div>

    <!-- Sectors -->
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <h3 class="text-lg font-semibold">Sectors</h3>
          <UBadge color="neutral" variant="subtle" size="sm">{{
            advertiser.sectors.length
          }}</UBadge>
        </div>
        <UButton
          icon="i-lucide-plus"
          color="neutral"
          variant="soft"
          size="sm"
          @click="showCreateSectorModal = true"
        >
          New sector
        </UButton>
      </div>

      <div class="space-y-2">
        <p class="text-xs text-muted uppercase tracking-wide">Brand sectors (click to exclude)</p>
        <p v-if="brandSectors.length === 0" class="text-sm text-muted">None yet.</p>
        <div v-else class="flex flex-wrap gap-2">
          <UButton
            v-for="sector in brandSectors"
            :key="sector.id"
            :color="excludedSectorIds.has(sector.id) ? 'error' : 'primary'"
            :variant="
              excludedSectorIds.has(sector.id)
                ? 'outline'
                : explicitSectorIds.has(sector.id)
                  ? 'solid'
                  : 'soft'
            "
            :class="{ 'line-through opacity-60': excludedSectorIds.has(sector.id) }"
            size="xs"
            :disabled="isToggling"
            :title="
              excludedSectorIds.has(sector.id)
                ? 'Excluded — click to re-include'
                : explicitSectorIds.has(sector.id)
                  ? 'Explicitly set — click to exclude'
                  : 'Inherited — click to exclude'
            "
            @click="toggleBrandSector(sector.id)"
          >
            <span v-if="sector.emoji" class="mr-1">{{ sector.emoji }}</span>
            {{ sector.name }}
          </UButton>
        </div>
      </div>

      <div v-if="otherSectors.length" class="space-y-2">
        <p class="text-xs text-muted uppercase tracking-wide">
          Other sectors (also added to the brand)
        </p>
        <div class="flex flex-wrap gap-2">
          <UButton
            v-for="sector in otherSectors"
            :key="sector.id"
            color="neutral"
            variant="ghost"
            size="xs"
            :disabled="isToggling"
            @click="addOtherSector(sector.id)"
          >
            <span v-if="sector.emoji" class="mr-1">{{ sector.emoji }}</span>
            {{ sector.name }}
          </UButton>
        </div>
      </div>
    </div>

    <UModal v-model:open="showCreateSectorModal">
      <template #content>
        <UCard>
          <template #header>
            <h3 class="text-lg font-semibold">Create Sector</h3>
          </template>
          <AdminSectorForm
            :sector="null"
            @submit="handleCreateSector"
            @cancel="showCreateSectorModal = false"
          />
        </UCard>
      </template>
    </UModal>

    <UModal v-model:open="showScopeModal">
      <template #content>
        <UCard>
          <template #header>
            <h3 class="text-lg font-semibold">Apply sector</h3>
          </template>
          <div class="p-4 space-y-4">
            <p class="text-sm text-muted">
              Where should
              <span class="font-medium text-default">
                {{ pendingSector?.emoji }} {{ pendingSector?.name }}
              </span>
              be applied?
            </p>
            <div class="flex flex-col gap-2">
              <UButton
                color="primary"
                variant="soft"
                class="justify-start"
                :disabled="isToggling"
                @click="applySectorToAdvertiser"
              >
                <UIcon name="i-lucide-user" class="mr-2" />
                Just this advertiser
              </UButton>
              <UButton
                color="neutral"
                variant="soft"
                class="justify-start"
                :disabled="isToggling"
                @click="applySectorToBrand"
              >
                <UIcon name="i-lucide-users" class="mr-2" />
                All advertisers (add to brand)
              </UButton>
            </div>
          </div>
        </UCard>
      </template>
    </UModal>
  </div>
</template>
